
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Database, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getEdgeFunctionUrl, getAnonKey } from '@/utils/supabaseUrl';

const SUPABASE_ANON_KEY = getAnonKey();
import { toast } from 'sonner';

interface ProcessingStats {
  processed: number;
  errors: number;
  totalProcessed: number;
  totalErrors: number;
  hasMore: boolean;
  nextOffset: number;
}

const KnowledgeEmbedderButton: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentDataSource, setCurrentDataSource] = useState<string>('');
  const [progress, setProgress] = useState<{ [key: string]: ProcessingStats }>({});
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const dataSources = [
    { name: 'b2b_products', label: 'B2B Products', priority: 1 },
    { name: 'local_shop_prices', label: 'Local Shop Prices', priority: 2 },
    { name: 'supermarket_prices', label: 'Supermarket Prices', priority: 3 },
    { name: 'farms', label: 'Farms Data', priority: 4 },
    { name: 'farm_prices', label: 'Farm Prices', priority: 5 },
    { name: 'procurements', label: 'Procurements', priority: 6 },
    { name: 'b2b_product_price_history', label: 'B2B Product Price History', priority: 7 },
    { name: 'chipchip_sales_data', label: 'ChipChip Sales Data', priority: 8 },
    { name: 'harvests', label: 'Harvests Data', priority: 9 },
    { name: 'suppliers', label: 'Suppliers Data', priority: 10 },
    { name: 'agents', label: 'Agents Data', priority: 11 },
    { name: 'products', label: 'Products', priority: 12 },
    { name: 'ecommerce_prices', label: 'Ecommerce Prices', priority: 13 },
    { name: 'distribution_center_prices', label: 'Distribution Center Prices', priority: 14 },
    { name: 'sunday_market_prices', label: 'Sunday Market Prices', priority: 15 },
    { name: 'farm_products', label: 'Farm Products', priority: 16 },
    { name: 'supplier_products', label: 'Supplier Products', priority: 17 },
    { name: 'supplier_categories', label: 'Supplier Categories', priority: 18 },
    { name: 'agent_products', label: 'Agent Products', priority: 19 },
    { name: 'agent_categories', label: 'Agent Categories', priority: 20 }
  ];

  const handleSourceToggle = (sourceName: string) => {
    setSelectedSources(prev => 
      prev.includes(sourceName) 
        ? prev.filter(s => s !== sourceName)
        : [...prev, sourceName]
    );
  };

  const handleSelectAll = () => {
    setSelectedSources(selectedSources.length === dataSources.length ? [] : dataSources.map(s => s.name));
  };

  const handlePopulateKnowledge = async () => {
    if (selectedSources.length === 0) {
      toast.error('Please select at least one data source to process.');
      return;
    }

    setIsLoading(true);
    setProgress({});
    
    try {
      console.log('Starting knowledge base population...');
      
      // Process only selected data sources sequentially
      const sourcesToProcess = dataSources.filter(source => selectedSources.includes(source.name));
      for (const source of sourcesToProcess) {
        setCurrentDataSource(source.label);
        await processDataSource(source.name, source.label);
      }
      
      const totalProcessed = Object.values(progress).reduce((sum, stats) => sum + stats.totalProcessed, 0);
      const totalErrors = Object.values(progress).reduce((sum, stats) => sum + stats.totalErrors, 0);
      
      toast.success(`Knowledge base populated! Processed ${totalProcessed} records with ${totalErrors} errors.`);
      
    } catch (error) {
      console.error('Error populating knowledge base:', error);
      toast.error('Failed to populate knowledge base. Please check the logs.');
    } finally {
      setIsLoading(false);
      setCurrentDataSource('');
    }
  };

  const processDataSource = async (dataSource: string, label: string) => {
    let offset = 0;
    const batchSize = 500; // Increased from 50
    let hasMore = true;
    
    while (hasMore) {
      try {
        console.log(`Processing ${label} batch at offset ${offset}...`);
        
        // Use URL parameters for proper data source selection with optimized parameters
        const url = new URL(getEdgeFunctionUrl('knowledge-embedder'));
        url.searchParams.set('source', dataSource);
        url.searchParams.set('batch_size', batchSize.toString());
        url.searchParams.set('offset', offset.toString());
        url.searchParams.set('limit', batchSize.toString());
        url.searchParams.set('parallel_batches', '3'); // Enable parallel processing
        
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey': SUPABASE_ANON_KEY
          }
        });
        
        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.error || `Failed to process ${label} batch`);
        }
        
        // Update progress
        setProgress(prev => ({
          ...prev,
          [dataSource]: {
            processed: result.stats.processed,
            errors: result.stats.errors,
            totalProcessed: (prev[dataSource]?.totalProcessed || 0) + result.stats.processed,
            totalErrors: (prev[dataSource]?.totalErrors || 0) + result.stats.errors,
            hasMore: result.stats.hasMore,
            nextOffset: result.stats.nextOffset
          }
        }));
        
        hasMore = result.stats.hasMore;
        offset = result.stats.nextOffset;
        
        console.log(`${label} batch completed: ${result.stats.processed} processed, ${result.stats.errors} errors`);
        
        // Reduced delay for faster processing
        await new Promise(resolve => setTimeout(resolve, 50));
        
      } catch (error) {
        console.error(`Error processing ${label} batch:`, error);
        toast.error(`Error processing ${label}: ${error.message}`);
        break;
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Select Data Sources:</h4>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSelectAll}
            disabled={isLoading}
          >
            {selectedSources.length === dataSources.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 gap-2">
          {dataSources.map((source) => (
            <div key={source.name} className="flex items-center space-x-2">
              <Checkbox
                id={source.name}
                checked={selectedSources.includes(source.name)}
                onCheckedChange={() => handleSourceToggle(source.name)}
                disabled={isLoading}
              />
              <label 
                htmlFor={source.name} 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {source.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <Button
        onClick={handlePopulateKnowledge}
        disabled={isLoading || selectedSources.length === 0}
        variant="outline"
        className="gap-2 w-full"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Database className="h-4 w-4" />
        )}
        {isLoading ? 'Populating...' : `Populate Selected Sources (${selectedSources.length})`}
      </Button>
      
      {isLoading && currentDataSource && (
        <div className="text-sm text-muted-foreground">
          Currently processing: {currentDataSource}
        </div>
      )}
      
      {Object.keys(progress).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Processing Progress:</h4>
          {Object.entries(progress).map(([source, stats]) => (
            <div key={source} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3 text-green-500" />
                {dataSources.find(ds => ds.name === source)?.label || source}
              </span>
              <span className="text-muted-foreground">
                {stats.totalProcessed} processed, {stats.totalErrors} errors
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeEmbedderButton;
